import type React from "react";

import { useState, useRef } from "react";

interface ZoomableImageProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  zoomLevel?: number;
  zoomWindowSize?: number;
  fallback?: React.ReactNode;
  onClick?: () => void;
  containerClassName?: string;
  className?: string;
}

export default function ZoomableImage({
  src,
  alt,
  width,
  height,
  zoomLevel = 1.8,
  fallback,
  zoomWindowSize = 300,
  onClick,
  containerClassName,
  className,
}: ZoomableImageProps) {
  const [showZoom, setShowZoom] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [imageError, setImageError] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);

  // Calculate background position for the zoomed view
  const backgroundPosition = `${
    -position.x * zoomLevel + zoomWindowSize / 2
  }px ${-position.y * zoomLevel + zoomWindowSize / 2}px`;

  // Handle mouse movement over the image
  const handleMouseMove = (e: React.MouseEvent) => {
    if (imageRef.current) {
      const { left, top } = imageRef.current.getBoundingClientRect();

      // Calculate cursor position relative to the image
      const x = e.clientX - left;
      const y = e.clientY - top;

      setPosition({ x, y });
      setMousePosition({ x: e.clientX, y: e.clientY });
    }
  };

  // Position the zoom window near the cursor but keep it on screen
  const getZoomWindowPosition = () => {
    // Default position is to the right of the cursor
    let left = mousePosition.x + 20;
    let top = mousePosition.y - zoomWindowSize / 2;

    // Check if window would go off the right side of the viewport
    if (left + zoomWindowSize > window.innerWidth) {
      // Position to the left of the cursor instead
      left = mousePosition.x - zoomWindowSize - 20;
    }

    // Ensure the zoom window stays within the vertical bounds of the viewport
    if (top < 0) {
      top = 0;
    } else if (top + zoomWindowSize > window.innerHeight) {
      top = window.innerHeight - zoomWindowSize;
    }

    return { left, top };
  };

  const zoomPosition = getZoomWindowPosition();

  // Default fallback component if none provided
  const defaultFallback = (
    <div className={` ${containerClassName}`}>
      <span className="text-gray-01" onClick={onClick}>
        Image not available
      </span>
    </div>
  );
  // Check if image source is valid
  const isValidSrc = src && src !== "" && !imageError;

  // If image source is invalid, render fallback
  if (!isValidSrc) {
    return <>{fallback || defaultFallback}</>;
  }

  return (
    <div
      className={`relative inline-block ${containerClassName}`}
      onClick={onClick}
    >
      <div
        ref={imageRef}
        className="cursor-crosshair overflow-hidden size-full bg-blue-200"
        onMouseEnter={() => setShowZoom(true)}
        onMouseLeave={() => setShowZoom(false)}
        onMouseMove={handleMouseMove}
      >
        {/* <RazorImageFallback
          loadSize={45}
          className="size-[70%] mx-auto block"
          url={src}
          //   styles={{
          //     width: width,
          //     height: height,
          //   }}
        >
          <figure className="size-full text-gray-02">
            No file found
          </figure>
        </RazorImageFallback> */}
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          className={`block ${className} size-[100%]`}
          // onError={() => setImageError(true)}
        />
      </div>

      {showZoom && isValidSrc && (
        <div
          className="fixed rounded-md shadow-lg border border-gray-200 overflow-hidden z-50"
          style={{
            width: `${zoomWindowSize}px`,
            height: `${zoomWindowSize}px`,
            left: `${zoomPosition.left}px`,
            top: `${zoomPosition.top}px`,
            backgroundImage: `url(${src})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${width * zoomLevel}px ${height * zoomLevel}px`,
            backgroundPosition,
          }}
        />
      )}
    </div>
  );
}
